import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { ShieldAlert } from 'lucide-react';
import Login from 'pages/login/Login';
import Logout from 'pages/logout/Logout';
import Registration from 'pages/registration/Registration';
import VerifyOTP from 'components/auth/VerifyOTP';
import Welcome from 'pages/welcome/Welcome';
import Rules from 'pages/rules/Rules';
import NewRule from 'pages/rules/NewRule';
import ForgotPassword from 'components/auth/ForgotPassword';
import ResetPassword from 'components/auth/ResetPassword';
import Accounts from 'pages/accounts/Accounts';
import Famousclicker from 'pages/landing/FamousClicker';
import AudienceSettingsTab from 'components/accounts/AudienceSettingsTab';
import AccountSettingsTab from 'components/accounts/AccountSettingsTab';
import AuthLayout from 'components/layouts/AuthLayout';
import DashboardLayout from 'components/layouts/DashboardLayout';
import AccountsLayout from 'components/layouts/AccountsLayout';
import Tabs from 'components/accounts/Tabs';
import ProtectedRoute from 'components/auth/ProtectedRoute';
import SubscriptionGuard from 'components/auth/SubscriptionGuard';
import Spinner from 'components/common/Spinner';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { RootState } from 'config/redux/store';

// Lazy-loaded page components (route-based code splitting)
const Dashboard = React.lazy(() => import('pages/dashboard/Dashboard'));
const Admin = React.lazy(() => import('pages/admin/AdminDashboard'));
const SubscriptionManagement = React.lazy(() => import('pages/admin/SubscriptionManagement'));
const AccountsAnalysis = React.lazy(() => import('pages/admin/AccountsAnalysis'));
const UserManagement = React.lazy(() => import('pages/superAdmin').then(m => ({ default: m.UserManagement })));
const AccountsManagement = React.lazy(() => import('pages/superAdmin').then(m => ({ default: m.AccountsManagement })));
const JobManagement = React.lazy(() => import('pages/superAdmin').then(m => ({ default: m.JobManagement })));
const QueueManagement = React.lazy(() => import('pages/superAdmin').then(m => ({ default: m.QueueManagement })));
const AnboxManagement = React.lazy(() => import('pages/superAdmin').then(m => ({ default: m.AnboxManagement })));
const ProxiesManagement = React.lazy(() => import('pages/superAdmin').then(m => ({ default: m.ProxiesManagement })));
const RuleDetail = React.lazy(() => import('pages/rules/RuleDetail'));
const Support = React.lazy(() => import('pages/support/Support'));
const OnboardingWizard = React.lazy(() => import('pages/onboarding/OnboardingWizard'));
const Subscriptions = React.lazy(() => import('pages/subscriptions/Subscriptions'));
const PaymentSuccess = React.lazy(() => import('pages/subscriptions/PaymentSuccess'));
const PaymentCancel = React.lazy(() => import('pages/subscriptions/PaymentCancel'));
const Billing = React.lazy(() => import('pages/billing/Billing'));
const Settings = React.lazy(() => import('pages/settings/Settings'));
const Posts = React.lazy(() => import('pages/actions/Posts'));
const Story = React.lazy(() => import('pages/actions/Story'));
const Messages = React.lazy(() => import('pages/actions/Messages'));
const Comments = React.lazy(() => import('pages/actions/Comments'));
const Likes = React.lazy(() => import('pages/actions/Likes'));
const Followings = React.lazy(() => import('pages/actions/Followings'));

// Define component props
interface RouterProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const CategoryGuard: React.FC<{ category: string; children: React.ReactNode }> = ({ category, children }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const subscriptionCategories = useSelector((state: RootState) => state.user.subscriptionCategories);

  if (subscriptionCategories.length > 0 && !subscriptionCategories.includes(category)) {
    return (
      <div className="flex flex-col items-center justify-center h-full" style={{ gap: '16px', padding: '48px' }}>
        <div className="flex items-center justify-center" style={{ width: '56px', height: '56px', borderRadius: '16px', background: isDark ? '#1D293D' : '#F1F5F9' }}>
          <ShieldAlert size={28} color={isDark ? '#62748E' : '#90A1B9'} />
        </div>
        <p style={{ fontSize: '16px', fontWeight: 600, color: isDark ? '#E2E8F0' : '#314158', textAlign: 'center' }}>
          {t('accessRestricted.title', 'Access Restricted')}
        </p>
        <p style={{ fontSize: '14px', fontWeight: 400, color: isDark ? '#90A1B9' : '#62748E', textAlign: 'center', maxWidth: '400px' }}>
          {t('accessRestricted.message', "You don't have access to this feature. Please upgrade your subscription.")}
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: '8px', padding: '8px 20px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }}
        >
          {t('accessRestricted.backToDashboard', 'Back to Dashboard')}
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

const Router: React.FC<RouterProps> = ({ setIsLoggedIn }) => {
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);

  return (
    <ErrorBoundary>
      <Suspense fallback={<Spinner size="large" text="Loading..." fullPage />}>
        <Routes>
        {/* Public Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/" element={<ErrorBoundary><Famousclicker /></ErrorBoundary>} />
          <Route path="/login" element={<ErrorBoundary><Login setIsLoggedIn={setIsLoggedIn} /></ErrorBoundary>} />
          <Route path="/logout" element={<ErrorBoundary><Logout /></ErrorBoundary>} />
          <Route path="/registration" element={<ErrorBoundary><Registration setIsLoggedIn={setIsLoggedIn} /></ErrorBoundary>} />
          <Route path="/verify-otp" element={<ErrorBoundary><VerifyOTP /></ErrorBoundary>} />
          <Route path="/welcome" element={<ErrorBoundary><Welcome setIsLoggedIn={setIsLoggedIn} /></ErrorBoundary>} />
          <Route path="/forgot-password" element={<ErrorBoundary><ForgotPassword /></ErrorBoundary>} />
          <Route path="/reset-password" element={<ErrorBoundary><ResetPassword /></ErrorBoundary>} />
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
    <Route element={<DashboardLayout setIsLoggedIn={setIsLoggedIn} />}>
      <Route path="/dashboard" element={<ErrorBoundary><SubscriptionGuard><Dashboard /></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/rules" element={<ErrorBoundary><SubscriptionGuard><Rules /></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/rules/new" element={<ErrorBoundary><SubscriptionGuard><NewRule /></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/rules/:ruleId" element={<ErrorBoundary><SubscriptionGuard><RuleDetail /></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/subscriptions" element={<ErrorBoundary><Subscriptions /></ErrorBoundary>} />
      <Route path="/onboarding" element={<ErrorBoundary><OnboardingWizard /></ErrorBoundary>} />

      <Route path="/billing" element={<ErrorBoundary><Billing /></ErrorBoundary>} />
      <Route path="/payment/success" element={<ErrorBoundary><PaymentSuccess /></ErrorBoundary>} />
      <Route path="/payment/cancel" element={<ErrorBoundary><PaymentCancel /></ErrorBoundary>} />
      <Route path="/actions/messages" element={<ErrorBoundary><SubscriptionGuard><CategoryGuard category="Messages"><Messages selectedUser={selectedUser ?? ''} /></CategoryGuard></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/actions/comments" element={<ErrorBoundary><SubscriptionGuard><CategoryGuard category="Comments"><Comments selectedUser={selectedUser ?? ''} /></CategoryGuard></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/actions/likes" element={<ErrorBoundary><SubscriptionGuard><CategoryGuard category="Likes"><Likes selectedUser={selectedUser ?? ''} /></CategoryGuard></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/actions/followings" element={<ErrorBoundary><SubscriptionGuard><CategoryGuard category="Following"><Followings selectedUser={selectedUser ?? ''} /></CategoryGuard></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/actions/posts" element={<ErrorBoundary><SubscriptionGuard><CategoryGuard category="Posts"><Posts selectedUser={selectedUser ?? ''} /></CategoryGuard></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/actions/story" element={<ErrorBoundary><SubscriptionGuard><CategoryGuard category="Story"><Story selectedUser={selectedUser ?? ''} /></CategoryGuard></SubscriptionGuard></ErrorBoundary>} />
      <Route path="/settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
      <Route path="/help" element={<ErrorBoundary><Navigate to="/support" replace /></ErrorBoundary>} />
      <Route path="/support" element={<ErrorBoundary><Support /></ErrorBoundary>} />
      <Route path="/accounts" element={<ErrorBoundary><SubscriptionGuard><Accounts /></SubscriptionGuard></ErrorBoundary>}>
        <Route path=":id" element={<ErrorBoundary><AccountsLayout /></ErrorBoundary>}>
          <Route path="tabs" element={<ErrorBoundary><Tabs /></ErrorBoundary>} />
          <Route path="audience-settings" element={<ErrorBoundary><AudienceSettingsTab /></ErrorBoundary>} />
          <Route path="account-settings" element={<ErrorBoundary><AccountSettingsTab /></ErrorBoundary>} />
        </Route>
      </Route>
    </Route>
  </Route>


        {/* Admin Routes with Role Restriction */}
        <Route element={<ProtectedRoute allowedRoles={['Admin', 'SuperAdmin']} />}>
          <Route element={<DashboardLayout setIsLoggedIn={setIsLoggedIn} />}>
            <Route path="/admin" element={<ErrorBoundary><Admin /></ErrorBoundary>} />
            <Route path="/admin/subscriptions" element={<ErrorBoundary><SubscriptionManagement /></ErrorBoundary>} />
            <Route path="/admin/accounts-analysis" element={<ErrorBoundary><AccountsAnalysis /></ErrorBoundary>} />
          </Route>
        </Route>

        {/* Super Admin Routes - Only SuperAdmin role */}
        <Route element={<ProtectedRoute allowedRoles={['SuperAdmin']} />}>
          <Route element={<DashboardLayout setIsLoggedIn={setIsLoggedIn} />}>
            <Route path="/super-admin/users" element={<ErrorBoundary><UserManagement /></ErrorBoundary>} />
            <Route path="/super-admin/accounts" element={<ErrorBoundary><AccountsManagement /></ErrorBoundary>} />
            <Route path="/super-admin/jobs" element={<ErrorBoundary><JobManagement /></ErrorBoundary>} />
            <Route path="/super-admin/queue" element={<ErrorBoundary><QueueManagement /></ErrorBoundary>} />
            <Route path="/super-admin/anbox" element={<ErrorBoundary><AnboxManagement /></ErrorBoundary>} />
            <Route path="/super-admin/proxies" element={<ErrorBoundary><ProxiesManagement /></ErrorBoundary>} />
          </Route>
        </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default Router;
